import { LoggingService } from '@adaptiva/core/logging';
import { IResourcePoolConfiguration } from '../interfaces/';
import { ResourcePool } from './resource-pool';

/**
 * @author Jesse Rogers <jesse.rogers@adaptiva.com>
 * @summary `ResourcePoolManager` is a base class for managing `ResourcePool`s of a specific type.
 *  It can either be extended or used as a standalone instance.
 * @see IResourcePoolConfiguration
 */
export class ResourcePoolManager<T = any> {

	/** Map of pool ID to `ResourcePool` */
	private _pools: Map<string, ResourcePool<T>>;

	constructor(private _logger: LoggingService) {
		this._pools = new Map<string, ResourcePool<T>>();
	}

	/**
	 * Retrieves a `ResourcePool` by ID. If the `ResourcePool` has expired, it will close the pool
	 * and return `null`
	 */
	public getPool(_id: string): ResourcePool<T> | null {
		this.cleanup();
		const _pool: ResourcePool<T> = this._pools.get(_id);

		if (!_pool || _pool.closed)
			return null;

		if (_pool.expired) {
			this._logger.warn(
				'ResourcePoolManager.getPool',
				`Attempted to join an expired ResourcePool: [${_id}]`
			)();
			this.closePool(_id);

			return null;
		}

		return _pool;
	}

	/**
	 * Returns a new `ResourcePool` from a given config after storing it locally.
	 * @returns `ResourcePool`
	 * @see ResourcePool
	 */
	public createPool(_config: IResourcePoolConfiguration): ResourcePool<T> {
		const _pool: ResourcePool<T> = new ResourcePool(_config);

		this._pools.set(_pool.id, _pool);

		return _pool;
	}

	/**
	 * Runs `ResourcePool` teardown logic and removes it from the local map
	 * @param _pool Can either be the pool's `id` or a `ResourcePool` instance
	 */
	public closePool(_pool: string | ResourcePool): void {
		if (typeof _pool === 'string')
			_pool = this._pools.get(_pool);

		this._removePool(_pool);
		this.cleanup();
	}

	/** Removes any and all expired `ResourcePool`s */
	public cleanup(): void {
		this._pools.forEach(_pool => {
			if (_pool.expired)
				this._removePool(_pool);
		});
	}

	private _removePool(_pool: ResourcePool): void {
		_pool.close();
		this._pools.delete(_pool.id);
	}

}
